import {
  type TrustCheckViewModel,
  useTrustChecksViewModel,
} from "@/hooks/matter-view-models/use-trust-checks-view-model";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import type {
  MatterSettlementCounter,
  MatterSettlementOffer,
  MatterSettlementDemand,
  Matter,
} from "@/lib/types";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
  calculateNetSettlementAmount,
  calculateSettlementTotals,
} from "@/lib/utils/matters/settlement-utils";

export type SettlementViewModelOffer = MatterSettlementOffer & {
  type: "Offer";
  netSettlementAmount: number;
  trustChecks: TrustCheckViewModel[];
};

export type SettlementViewModelDemand = MatterSettlementDemand & {
  type: "Demand";
  netSettlementAmount: number;
  trustChecks: TrustCheckViewModel[];
};

export type SettlementViewModelCounter = MatterSettlementCounter & {
  type: "Counter";
  netSettlementAmount: number;
  trustChecks: TrustCheckViewModel[];
};

export type SettlementViewModel =
  | SettlementViewModelOffer
  | SettlementViewModelDemand
  | SettlementViewModelCounter;

type MatterSettlementCollections = Partial<Matter> & {
  settlementCounters?: MatterSettlementCounter[];
  settlementDemands?: MatterSettlementDemand[];
  settlementOffers?: MatterSettlementOffer[];
};

const EMPTY_SETTLEMENT_COUNTERS: MatterSettlementCounter[] = [];
const EMPTY_SETTLEMENT_DEMANDS: MatterSettlementDemand[] = [];
const EMPTY_SETTLEMENT_OFFERS: MatterSettlementOffer[] = [];

export const useSettlementsViewModel = (matterId: string) => {
  const { data: matterData } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );
  const matter = matterData.data;

  const trustChecksViewModel = useTrustChecksViewModel(matterId);

  const matterCollections = matter as MatterSettlementCollections;
  const offers: MatterSettlementOffer[] =
    matterCollections.settlementOffers ?? EMPTY_SETTLEMENT_OFFERS;
  const demands: MatterSettlementDemand[] =
    matterCollections.settlementDemands ?? EMPTY_SETTLEMENT_DEMANDS;
  const counters: MatterSettlementCounter[] =
    matterCollections.settlementCounters ?? EMPTY_SETTLEMENT_COUNTERS;

  const offerVMs: SettlementViewModelOffer[] = offers.map((offer) => {
    const isOfferMedPay = offer.coverageType
      ? offer.coverageType === "MedPay"
      : (offer.medPay ?? false);
    const trustChecks = trustChecksViewModel.filter((tc) =>
      tc.connections.some((c) => c.settlementOfferId === offer.id),
    );
    return {
      ...offer,
      type: "Offer",
      netSettlementAmount: calculateNetSettlementAmount(
        offer.amount ?? 0,
        isOfferMedPay,
        offer.medPayRefund ?? false,
        offer.medPayRefundAmount ?? 0,
      ),
      trustChecks,
    };
  });

  const demandVMs: SettlementViewModelDemand[] = demands.map((demand) => {
    const trustChecks = trustChecksViewModel.filter((tc) =>
      tc.connections.some((c) => c.settlementDemandId === demand.id),
    );
    const isMedPayCoverage = demand.coverageType === "MedPay";
    return {
      ...demand,
      type: "Demand",
      netSettlementAmount: calculateNetSettlementAmount(
        demand.amount ?? 0,
        isMedPayCoverage,
        demand.medPayRefund ?? false,
        demand.medPayRefundAmount ?? 0,
      ),
      trustChecks,
    };
  });

  const counterVMs: SettlementViewModelCounter[] = counters.map((counter) => {
    const trustChecks = trustChecksViewModel.filter((tc) =>
      tc.connections.some((c) => c.settlementCounterId === counter.id),
    );
    const isCounterMedPay = counter.coverageType === "MedPay";
    return {
      ...counter,
      type: "Counter",
      netSettlementAmount: calculateNetSettlementAmount(
        counter.amount ?? 0,
        isCounterMedPay,
        false,
        0,
      ),
      trustChecks,
    };
  });

  const parseDate = (iso?: string) => {
    const t = iso ? Date.parse(iso) : NaN;
    return Number.isNaN(t) ? 0 : t;
  };

  // Sort newest→oldest within each type group
  offerVMs.sort(
    (a, b) => parseDate(b.dateReceived) - parseDate(a.dateReceived),
  );
  demandVMs.sort((a, b) => parseDate(b.dateSent) - parseDate(a.dateSent));
  counterVMs.sort((a, b) => parseDate(b.dateSent) - parseDate(a.dateSent));

  // Calculate settlement totals
  const settlements = [...offerVMs, ...demandVMs, ...counterVMs];
  const settlementData = settlements.map((settlement) => ({
    amount: settlement.amount ?? 0,
    accepted:
      settlement.type === "Offer"
        ? (settlement.accepted ?? false)
        : settlement.type === "Counter"
          ? (settlement.accepted ?? false)
          : settlement.coverageType === "MedPay" &&
            (settlement.accepted ?? false),
    attorneyFeePercentage: settlement.attorneyFeePercentage ?? 0,
    medPay: settlement.coverageType === "MedPay",
    medPayRefund:
      "medPayRefund" in settlement ? (settlement.medPayRefund ?? false) : false,
    medPayRefundAmount:
      "medPayRefundAmount" in settlement
        ? (settlement.medPayRefundAmount ?? 0)
        : 0,
  }));

  const { totalAcceptedSettlements, netToFirm } =
    calculateSettlementTotals(settlementData);

  return {
    settlements: [
      ...offerVMs,
      ...demandVMs,
      ...counterVMs,
    ] as SettlementViewModel[],
    totalAcceptedSettlements,
    netToFirm,
  };
};
